import { Injectable, inject } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { filter, map, skipWhile, switchMap, withLatestFrom } from 'rxjs/operators';

import { LabelService } from '../../features/label/services/label.service';
import { Permission } from '../../features/shared/models/api/permissions.enum';
import { WebSocketEntities } from '../../features/shared/models/web-socket/web-socket.models';
import { createWebSocketEffect } from '../../features/shared/services/web-socket/create-web-socket-effect';
import { WebSocketService } from '../../features/shared/services/web-socket/web-socket.service';
import { reportEffectError } from '../../features/shared/utils/report-effect-error';
import { RootState } from '../root-state.model';
import { LabelActions } from './label.actions';
import { UserProfileService } from '../../features/settings/services/user-profile.service';
import { UserProfileSelectors } from '../user-profile/user-profile.selectors';

@Injectable()
export class LabelEffects {
  private actions$ = inject(Actions);
  private readonly store = inject<Store<RootState>>(Store);
  private readonly labelService = inject(LabelService);
  private readonly userProfileService = inject(UserProfileService);
  private readonly webSocketService = inject(WebSocketService);

  loadLabels$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(LabelActions.DownloadLabels),
      withLatestFrom(this.store.select(UserProfileSelectors.permissions)),
      skipWhile(([, permissions]) => !permissions),
      filter(([, permissions]) => permissions.includes(Permission.READ_LABEL)),
      switchMap(() => {
        return this.labelService.getAll().pipe(
          map((labels) => LabelActions.DownloadLabelsSuccess({ labels })),
          reportEffectError(LabelActions.DownloadLabelsFail)
        );
      })
    )
  );

  assignLabel$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(LabelActions.AssignLabel),
      switchMap((action) => {
        return this.labelService.assignLabel(action.labelId, action.entityId, action.entity).pipe(
          map(() => LabelActions.AssignLabelSuccess()),
          reportEffectError(LabelActions.AssignLabelFail)
        );
      })
    )
  );

  removeLabel$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(LabelActions.RemoveLabel),
      switchMap((action) => {
        return this.labelService.removeLabel(action.labelId, action.entityId, action.entity).pipe(
          map(() => LabelActions.RemoveLabelSuccess()),
          reportEffectError(LabelActions.RemoveLabelFail)
        );
      })
    )
  );

  setLabels$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(LabelActions.SetLabels),
      switchMap(({ labelIds, entityIds, entity }) => {
        return this.labelService.setLabels(labelIds, entityIds, entity).pipe(
          map(() => LabelActions.SetLabelsSuccess()),
          reportEffectError(LabelActions.SetLabelsFail)
        );
      })
    )
  );

  setDefaultLabels$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(LabelActions.SetDefaultLabels),
      switchMap(({ labels }) => {
        return this.userProfileService
          .updateUserSettings({
            defaultLabelIds: labels,
          })
          .pipe(
            map(() => LabelActions.SetDefaultLabelsSuccess({ labelIds: labels })),
            reportEffectError(LabelActions.SetDefaultLabelsFail)
          );
      })
    )
  );

  addLabel$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(LabelActions.AddLabel),
      switchMap(({ label }) => {
        return this.labelService.add(label).pipe(
          map(() => LabelActions.AddLabelSuccess()),
          reportEffectError(LabelActions.AddLabelFail)
        );
      })
    )
  );

  wsEffect$: Observable<Action> = createWebSocketEffect(
    this.store,
    this.actions$,
    this.webSocketService,
    WebSocketEntities.Label,
    () => LabelActions.DownloadLabels(),
    false,
    true
  );
}
